import { useMemo, useState } from "react";
import { LIMITS, type ExpenseBody, type GroupState } from "@psplit/ledger-core";
import { formatPaise, paiseToInputText, parseAmountToPaise } from "../lib/money.js";
import { displayName, resolveMember } from "../lib/names.js";
import { errorMessage } from "../lib/errors.js";
import { Modal } from "./Modal.js";

export function ExpenseForm({
  state,
  me,
  initial,
  allowRecurring,
  onSave,
  onVoid,
  onClose,
}: {
  state: GroupState;
  me: string;
  initial: ExpenseBody | null; // editing an existing entry when set
  allowRecurring: boolean;
  onSave: (body: ExpenseBody, repeatDay: number | null) => Promise<void>;
  onVoid: (() => Promise<void>) | null;
  onClose: () => void;
}) {
  const activeMembers = useMemo(() => state.members.filter((m) => m.active), [state]);
  const label = (pub: string) =>
    displayName(state, pub) + (resolveMember(state, pub)?.isGhost ? " (no app)" : "");

  const [desc, setDesc] = useState(initial?.desc ?? "");
  const [amountText, setAmountText] = useState(initial ? paiseToInputText(initial.amount) : "");
  const [paidBy, setPaidBy] = useState(initial?.paidBy ?? me);
  const [splitType, setSplitType] = useState<"equal" | "exact">(initial?.split.type ?? "equal");
  const [participants, setParticipants] = useState<Set<string>>(
    () => new Set(initial?.split.participants ?? activeMembers.map((m) => m.pubKey)),
  );
  const [exactTexts, setExactTexts] = useState<Record<string, string>>(() => {
    if (initial?.split.type !== "exact") return {};
    const out: Record<string, string> = {};
    initial.split.participants.forEach((p, i) => {
      const amounts = (initial.split as { amounts: number[] }).amounts;
      out[p] = paiseToInputText(amounts[i] as number);
    });
    return out;
  });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [repeat, setRepeat] = useState(false);
  const [repeatDayText, setRepeatDayText] = useState(String(new Date().getDate()));

  const amount = parseAmountToPaise(amountText);
  const chosen = activeMembers.filter((m) => participants.has(m.pubKey)).map((m) => m.pubKey);
  const exactSum = chosen.reduce((sum, p) => sum + (parseAmountToPaise(exactTexts[p] ?? "") ?? 0), 0);

  const toggle = (pub: string) => {
    setParticipants((prev) => {
      const next = new Set(prev);
      if (next.has(pub)) next.delete(pub);
      else next.add(pub);
      return next;
    });
  };

  const submit = async () => {
    setError("");
    if (desc.trim() === "") return setError("Description is required.");
    if (amount === null) return setError("Amount must be a positive number with at most 2 decimals.");
    if (chosen.length === 0) return setError("Pick at least one participant.");
    let body: ExpenseBody;
    if (splitType === "equal") {
      body = { desc: desc.trim(), amount, paidBy, split: { type: "equal", participants: chosen } };
    } else {
      const amounts = chosen.map((p) => parseAmountToPaise(exactTexts[p] ?? ""));
      if (amounts.some((a) => a === null)) return setError("Every participant needs an exact share.");
      if (exactSum !== amount) {
        return setError(
          `Exact shares add to ${formatPaise(exactSum)}, but the total is ${formatPaise(amount)}.`,
        );
      }
      body = {
        desc: desc.trim(),
        amount,
        paidBy,
        split: { type: "exact", participants: chosen, amounts: amounts as number[] },
      };
    }
    let repeatDay: number | null = null;
    if (allowRecurring && repeat) {
      const day = Number.parseInt(repeatDayText, 10);
      if (!Number.isSafeInteger(day) || day < 1 || day > 31) {
        return setError("Repeat day must be between 1 and 31.");
      }
      repeatDay = day;
    }
    setBusy(true);
    try {
      await onSave(body, repeatDay);
      onClose();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal onClose={onClose}>
        <h3>{initial ? "Edit expense" : "Add expense"}</h3>
        <label>
          What for
          <input value={desc} onChange={(e) => setDesc(e.target.value)} maxLength={LIMITS.desc} placeholder="Dinner, cab, tickets…" />
        </label>
        <label>
          Amount (₹)
          <input value={amountText} onChange={(e) => setAmountText(e.target.value)} inputMode="decimal" placeholder="0.00" />
        </label>
        <label>
          Who paid?
          <select value={paidBy} onChange={(e) => setPaidBy(e.target.value)}>
            {activeMembers.map((m) => (
              <option key={m.pubKey} value={m.pubKey}>
                {label(m.pubKey)}
                {m.pubKey === me ? " (you)" : ""}
              </option>
            ))}
          </select>
        </label>
        <div className="row">
          <span>Split</span>
          <div className="seg">
            <button className={splitType === "equal" ? "on" : ""} onClick={() => setSplitType("equal")}>
              Equally
            </button>
            <button className={splitType === "exact" ? "on" : ""} onClick={() => setSplitType("exact")}>
              Exact amounts
            </button>
          </div>
        </div>
        <div className="participants">
          {activeMembers.map((m) => (
            <div key={m.pubKey} className="participant">
              <label className="check">
                <input
                  type="checkbox"
                  checked={participants.has(m.pubKey)}
                  onChange={() => toggle(m.pubKey)}
                />
                {label(m.pubKey)}
                {m.pubKey === me ? " (you)" : ""}
              </label>
              {splitType === "exact" && participants.has(m.pubKey) && (
                <input
                  className="exact"
                  inputMode="decimal"
                  placeholder="0.00"
                  value={exactTexts[m.pubKey] ?? ""}
                  onChange={(e) =>
                    setExactTexts((prev) => ({ ...prev, [m.pubKey]: e.target.value }))
                  }
                />
              )}
            </div>
          ))}
        </div>
        {splitType === "exact" && amount !== null && (
          <div className={exactSum === amount ? "hint ok" : "hint"}>
            shares {formatPaise(exactSum)} of {formatPaise(amount)}
          </div>
        )}
        {allowRecurring && (
          <div className="row gap wrap">
            <label className="check">
              <input type="checkbox" checked={repeat} onChange={(e) => setRepeat(e.target.checked)} />
              Repeat monthly (starts next month)
            </label>
            {repeat && (
              <label>
                Day of month
                <input
                  className="exact"
                  inputMode="numeric"
                  value={repeatDayText}
                  onChange={(e) => setRepeatDayText(e.target.value)}
                />
              </label>
            )}
          </div>
        )}
        {error !== "" && <div className="error">{error}</div>}
        <div className="row gap">
          <button className="btn primary" disabled={busy} onClick={() => void submit()}>
            {initial ? "Save changes" : "Add expense"}
          </button>
          {onVoid && (
            <button
              className="btn danger"
              disabled={busy}
              onClick={() => {
                setBusy(true);
                void onVoid()
                  .then(onClose)
                  .catch((e: unknown) => setError(errorMessage(e)))
                  .finally(() => setBusy(false));
              }}
            >
              Void entry
            </button>
          )}
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
        </div>
    </Modal>
  );
}
